/**
 * Where this installation sends and receives, and who is allowed to.
 *
 * Four things on one page, because they are one decision: what the portal
 * talks to, how it is configured, which machines may write to it, and who is
 * told when something happens.
 *
 * **It opens honestly on "no connector".** That is the normal state of a fresh
 * installation and of one that will never have an ERP, so the empty state
 * explains what still works rather than presenting a blank form as a gap to
 * fill.
 *
 * **The settings are a key/value list, not a form.** Their shape belongs to
 * the connector — one asks for a server and a database, another for a URL and
 * a credential — so a fixed form here would have to change every time a
 * connector is added, which is the exact coupling the connector boundary
 * exists to prevent.
 *
 * **A credential is masked and stays masked.** The API returns bullets for any
 * key that names a secret and treats a submitted bullet string as "unchanged",
 * so an operator can change the server without retyping the password, and this
 * screen never holds the real value.
 *
 * **A token is shown once.** After it is minted the panel keeps it on screen
 * until dismissed, because there is no endpoint that could show it again.
 */

import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCan } from "@hardauto/core-access";
import type { ApiClient } from "@hardauto/core-api";
import { useAction, useResource } from "@hardauto/core-data";
import { EM_DASH, moment } from "@hardauto/core-i18n";
import { Field } from "@hardauto/core-forms";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  DataTable,
  EmptyState,
  Input,
  Page,
  StatusDot,
  useToast,
  type Column,
} from "@hardauto/core-ui";

import {
  EVENT_KEYS,
  IntegrationApi,
  MASKED,
  type MintedAccount,
  type ServiceAccount,
  type Webhook,
  type WebhookEvent,
} from "../api";
import { NAMESPACE } from "../i18n";
import "./integration.css";

const EVENTS: readonly WebhookEvent[] = [
  "order.placed",
  "order.approved",
  "order.cancelled",
  "shipment.recorded",
  "account.requested",
];

/** One row of the connector's settings, while it is being edited. */
interface SettingRow {
  key: string;
  value: string;
}

/**
 * A settings value as editable text.
 *
 * The document's shape belongs to the connector, so a value may be a string, a
 * number, a boolean, or a nested object. A two-column editor can only be
 * honest about the first three; anything else round-trips as its JSON text,
 * which is at least readable and editable rather than `[object Object]`.
 *
 * Values are saved back as text. A connector reads its own settings and knows
 * which of them are numbers — that is what "the shape belongs to the
 * connector" means, and parsing them here would be this screen having an
 * opinion about a schema it does not own.
 */
function asText(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

export function ConnectorScreen({ client }: { client: ApiClient }) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const api = useMemo(() => new IntegrationApi(client), [client]);
  const toast = useToast();
  const action = useAction();
  const mayConfigure = useCan("integration.connector.write");
  const mayIssue = useCan("integration.credential.write");

  const connectorFetcher = useCallback(() => api.connector(), [api]);
  const connector = useResource(connectorFetcher);

  const availableFetcher = useCallback(() => api.availableConnectors(), [api]);
  const available = useResource(availableFetcher);

  // Not requested at all without the permission, and the two cards below are
  // not rendered either. Fetching anyway put two 403s in the network log and
  // two "impossible de charger cette page" panels on screen — which reads as
  // a broken page rather than as a section this role has no business seeing.
  // Seen in the browser, not reasoned about.
  const accountsFetcher = useCallback(
    () => (mayIssue ? api.listAccounts() : Promise.resolve({ items: [] })),
    [api, mayIssue],
  );
  const accounts = useResource(accountsFetcher);

  const webhooksFetcher = useCallback(
    () => (mayIssue ? api.listWebhooks() : Promise.resolve({ items: [] })),
    [api, mayIssue],
  );
  const webhooks = useResource(webhooksFetcher);

  // The connector's form. Seeded from the loaded configuration the first time
  // it arrives, and edited locally after that — a controlled form re-seeded on
  // every reload would discard what somebody was typing.
  const [draft, setDraft] = useState<{
    code: string;
    label: string;
    enabled: boolean;
    settings: SettingRow[];
  } | null>(null);

  const form =
    draft ??
    (connector.data
      ? {
          code: connector.data.code,
          label: connector.data.label,
          enabled: connector.data.is_enabled,
          settings: Object.entries(connector.data.settings).map(([key, value]) => ({
            key,
            value: asText(value),
          })),
        }
      : { code: "", label: "", enabled: false, settings: [] as SettingRow[] });

  function edit(patch: Partial<typeof form>) {
    setDraft({ ...form, ...patch });
  }

  const [minted, setMinted] = useState<MintedAccount | null>(null);
  const [accountName, setAccountName] = useState("");
  const [accountEntities, setAccountEntities] = useState("");

  const [hookUrl, setHookUrl] = useState("");
  const [hookSecret, setHookSecret] = useState("");
  const [hookEvents, setHookEvents] = useState<WebhookEvent[]>(["order.placed"]);

  const accountColumns: Column<ServiceAccount>[] = [
    { key: "name", header: t("connector.accountColumn.name"), render: (row) => row.name },
    {
      key: "prefix",
      header: t("connector.accountColumn.prefix"),
      render: (row) => <code className="hardauto-code">{row.token_prefix}…</code>,
    },
    {
      key: "entities",
      header: t("connector.accountColumn.entities"),
      render: (row) =>
        row.entities.length === 0 ? (
          <span className="hardauto-hint">{t("connector.accountAll")}</span>
        ) : (
          row.entities.join(", ")
        ),
    },
    {
      key: "lastUsed",
      header: t("connector.accountColumn.lastUsed"),
      render: (row) => moment(row.last_used_at, i18n.language, t("common.never")),
    },
    {
      key: "state",
      header: t("connector.accountColumn.state"),
      render: (row) => (
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <StatusDot
            label={row.is_active ? t("connector.active") : t("connector.revoked")}
            tone={row.is_active ? "success" : "neutral"}
          />
          {mayIssue && row.is_active ? (
            <Button
              variant="ghost"
              size="sm"
              busy={action.isBusy(`revoke:${row.id}`)}
              busyLabel={t("common.saving")}
              onClick={() =>
                void action.run(`revoke:${row.id}`, async () => {
                  try {
                    await api.revokeAccount(row.id);
                    accounts.reload();
                  } catch {
                    toast.show(t("error.title"), { tone: "danger" });
                  }
                })
              }
            >
              {t("connector.revoke")}
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  const webhookColumns: Column<Webhook>[] = [
    {
      key: "url",
      header: t("connector.webhookColumn.url"),
      render: (row) => <code className="hardauto-code">{row.url}</code>,
    },
    {
      key: "events",
      header: t("connector.webhookColumn.events"),
      render: (row) => row.events.map((event) => t(`event.${EVENT_KEYS[event]}`)).join(", "),
    },
    {
      key: "lastDelivery",
      header: t("connector.webhookColumn.lastDelivery"),
      render: (row) =>
        row.last_delivery_at
          ? `${moment(row.last_delivery_at, i18n.language)} · ${row.last_status ?? EM_DASH}`
          : t("common.never"),
    },
    {
      key: "failures",
      header: t("connector.webhookColumn.failures"),
      render: (row) => (
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <Badge tone={row.failure_count > 0 ? "warning" : "neutral"}>
            {String(row.failure_count)}
          </Badge>
          {mayIssue ? (
            <Button
              variant="ghost"
              size="sm"
              busy={action.isBusy(`hook:${row.id}`)}
              busyLabel={t("common.saving")}
              onClick={() =>
                void action.run(`hook:${row.id}`, async () => {
                  try {
                    await api.removeWebhook(row.id);
                    webhooks.reload();
                  } catch {
                    toast.show(t("error.title"), { tone: "danger" });
                  }
                })
              }
            >
              {t("common.delete")}
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <Page title={t("connector.title")} description={t("connector.intro")} eyebrow={t("group")}>
      {/* A failed load says so. Without this the page rendered an empty form
          on a request that had been refused — seen when the dev rate limiter
          answered 429 — and an operator would have typed a configuration into
          a screen that was never going to save it. */}
      {connector.failed ? (
        <Alert tone="danger" title={t("error.load")}>
          <Button variant="secondary" onClick={connector.reload}>
            {t("common.refresh")}
          </Button>
        </Alert>
      ) : null}

      {/* One notice, not two. "No connector configured" and "this build has
          no connector module" are the same sentence to a reader when both are
          true, and the second only becomes news when a code IS configured —
          which is the case where the queue would otherwise never drain with
          nothing on screen to explain why. */}
      {!connector.failed && connector.data?.is_absent ? (
        <Alert tone="info" title={t("connector.absent")}>
          {t("connector.absentDetail")}
        </Alert>
      ) : available.data && available.data.length === 0 ? (
        <Alert tone="warning" title={t("connector.absent")}>
          {t("connector.noneInstalled")}
        </Alert>
      ) : null}

      {/* No form on a failed load. Rendering it with a working Save button was
          the other half of the same bug: the field would be empty, the save
          would go through, and an operator would have replaced a live
          configuration with a blank one because a GET was rate-limited. */}
      {connector.failed ? null : (
        <Card title={t("connector.settings")} description={t("connector.settingsIntro")}>
          <form
            className="hardauto-integration-form"
            onSubmit={(event) => {
              event.preventDefault();
              void action.run("connector", async () => {
                try {
                  const settings: Record<string, unknown> = {};
                  for (const row of form.settings) {
                    if (row.key.trim()) settings[row.key.trim()] = row.value;
                  }
                  await api.saveConnector({
                    code: form.code.trim(),
                    label: form.label.trim(),
                    settings,
                    is_enabled: form.enabled,
                  });
                  setDraft(null);
                  connector.reload();
                  toast.show(t("connector.saved"));
                } catch {
                  toast.show(t("error.title"), { tone: "danger" });
                }
              });
            }}
          >
            <Field label={t("connector.code")} hint={t("connector.codePlaceholder")}>
              {(props) => (
                <Input
                  {...props}
                  value={form.code}
                  disabled={!mayConfigure}
                  onChange={(event) => edit({ code: event.target.value })}
                />
              )}
            </Field>
            <Field label={t("connector.label")}>
              {(props) => (
                <Input
                  {...props}
                  value={form.label}
                  disabled={!mayConfigure}
                  onChange={(event) => edit({ label: event.target.value })}
                />
              )}
            </Field>

            <div className="hardauto-settings">
              {form.settings.map((row, index) => (
                <div className="hardauto-settings__row" key={`${row.key}-${index}`}>
                  <Field label={t("connector.settingKey")}>
                    {(props) => (
                      <Input
                        {...props}
                        value={row.key}
                        disabled={!mayConfigure}
                        onChange={(event) => {
                          const next = [...form.settings];
                          next[index] = { ...row, key: event.target.value };
                          edit({ settings: next });
                        }}
                      />
                    )}
                  </Field>
                  <Field
                    label={t("connector.settingValue")}
                    hint={row.value === MASKED ? t("connector.maskedHint") : undefined}
                  >
                    {(props) => (
                      <Input
                        {...props}
                        value={row.value}
                        disabled={!mayConfigure}
                        onChange={(event) => {
                          const next = [...form.settings];
                          next[index] = { ...row, value: event.target.value };
                          edit({ settings: next });
                        }}
                      />
                    )}
                  </Field>
                  {mayConfigure ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      type="button"
                      onClick={() =>
                        edit({ settings: form.settings.filter((_, at) => at !== index) })
                      }
                    >
                      {t("common.delete")}
                    </Button>
                  ) : null}
                </div>
              ))}
            </div>

            <Checkbox
              label={t("connector.enabled")}
              checked={form.enabled}
              disabled={!mayConfigure}
              onChange={(event) => edit({ enabled: event.target.checked })}
            />
            <p className="hardauto-hint">{t("connector.enabledHint")}</p>

            {mayConfigure ? (
              <div className="hardauto-integration-form__actions">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => edit({ settings: [...form.settings, { key: "", value: "" }] })}
                >
                  {t("connector.addSetting")}
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  busy={action.isBusy("connector")}
                  busyLabel={t("common.saving")}
                >
                  {t("common.save")}
                </Button>
              </div>
            ) : null}
          </form>
        </Card>
      )}

      <Card title={t("connector.endpoint")} description={t("connector.endpointIntro")}>
        {/* The exact request a connector makes, so nobody has to read the
            OpenAPI schema to send the first record. `{tenant}` and `{entity}`
            are left as placeholders on purpose: the tenant is this
            installation's slug, and the entity is one of the rows above. */}
        <pre className="hardauto-endpoint">
          {`POST /api/v1/integration/sync/{tenant}/{entity}
Authorization: Bearer hai_…
Content-Type: application/json

{ "reference": "85214", "designation": "PLAQUETTES AVANT" }`}
        </pre>
      </Card>

      {mayIssue ? (
        <Card
          title={t("connector.accounts")}
          description={t("connector.accountsIntro")}
          actions={
            mayIssue ? (
              <Button
                variant="primary"
                busy={action.isBusy("mint")}
                busyLabel={t("common.saving")}
                disabled={!accountName.trim()}
                onClick={() =>
                  void action.run("mint", async () => {
                    try {
                      const created = await api.mintAccount({
                        name: accountName.trim(),
                        entities: accountEntities
                          .split(",")
                          .map((entry) => entry.trim())
                          .filter(Boolean),
                      });
                      setMinted(created);
                      setAccountName("");
                      setAccountEntities("");
                      accounts.reload();
                    } catch {
                      toast.show(t("error.title"), { tone: "danger" });
                    }
                  })
                }
              >
                {t("connector.mint")}
              </Button>
            ) : undefined
          }
        >
          {minted ? (
            <div className="hardauto-token">
              <strong>{t("connector.tokenOnce")}</strong>
              <code className="hardauto-token__value">{minted.token}</code>
              <p className="hardauto-hint">{minted.notice}</p>
              <div className="hardauto-integration-form__actions">
                <Button
                  variant="secondary"
                  onClick={() => {
                    // `clipboard` is absent in some browsers and in jsdom, so
                    // the button degrades to the value being selectable rather
                    // than throwing.
                    void navigator.clipboard?.writeText(minted.token).then(
                      () => toast.show(t("connector.copied")),
                      () => undefined,
                    );
                  }}
                >
                  {t("connector.copy")}
                </Button>
                <Button variant="ghost" onClick={() => setMinted(null)}>
                  {t("common.cancel")}
                </Button>
              </div>
            </div>
          ) : null}

          {mayIssue ? (
            <div className="hardauto-integration-form">
              <Field label={t("connector.mintName")} hint={t("connector.mintNamePlaceholder")}>
                {(props) => (
                  <Input
                    {...props}
                    value={accountName}
                    onChange={(event) => setAccountName(event.target.value)}
                  />
                )}
              </Field>
              <Field label={t("connector.mintEntities")}>
                {(props) => (
                  <Input
                    {...props}
                    value={accountEntities}
                    onChange={(event) => setAccountEntities(event.target.value)}
                  />
                )}
              </Field>
            </div>
          ) : null}

          <DataTable<ServiceAccount>
            caption={t("connector.accounts")}
            columns={accountColumns}
            rows={accounts.data?.items ?? []}
            rowKey={(row) => row.id}
            loading={accounts.loading}
            loadingLabel={t("connector.accounts")}
            error={
              accounts.failed
                ? { title: t("error.load"), onRetry: accounts.reload, retryLabel: t("error.retry") }
                : undefined
            }
            empty={{ title: t("connector.accountsEmpty") }}
          />
        </Card>
      ) : null}

      {mayIssue ? (
        <Card
          title={t("connector.webhooks")}
          description={t("connector.webhooksIntro")}
          actions={
            mayIssue ? (
              <Button
                variant="primary"
                busy={action.isBusy("hook")}
                busyLabel={t("common.saving")}
                disabled={
                  !hookUrl.trim() || hookSecret.trim().length < 16 || hookEvents.length === 0
                }
                onClick={() =>
                  void action.run("hook", async () => {
                    try {
                      await api.addWebhook({
                        url: hookUrl.trim(),
                        secret: hookSecret,
                        events: hookEvents,
                      });
                      setHookUrl("");
                      setHookSecret("");
                      webhooks.reload();
                    } catch {
                      toast.show(t("error.title"), { tone: "danger" });
                    }
                  })
                }
              >
                {t("connector.addWebhook")}
              </Button>
            ) : undefined
          }
        >
          {mayIssue ? (
            <div className="hardauto-integration-form">
              <Field label={t("connector.webhookUrl")}>
                {(props) => (
                  <Input
                    {...props}
                    value={hookUrl}
                    onChange={(event) => setHookUrl(event.target.value)}
                  />
                )}
              </Field>
              <Field label={t("connector.webhookSecret")}>
                {(props) => (
                  <Input
                    {...props}
                    type="password"
                    value={hookSecret}
                    onChange={(event) => setHookSecret(event.target.value)}
                  />
                )}
              </Field>
              <div>
                {EVENTS.map((event) => (
                  <Checkbox
                    key={event}
                    label={t(`event.${EVENT_KEYS[event]}`)}
                    checked={hookEvents.includes(event)}
                    onChange={(changed) =>
                      setHookEvents(
                        changed.target.checked
                          ? [...hookEvents, event]
                          : hookEvents.filter((entry) => entry !== event),
                      )
                    }
                  />
                ))}
              </div>
            </div>
          ) : null}

          {webhooks.data && webhooks.data.items.length === 0 ? (
            <EmptyState title={t("connector.webhooksEmpty")} />
          ) : (
            <DataTable<Webhook>
              caption={t("connector.webhooks")}
              columns={webhookColumns}
              rows={webhooks.data?.items ?? []}
              rowKey={(row) => row.id}
              loading={webhooks.loading}
              loadingLabel={t("connector.webhooks")}
              error={
                webhooks.failed
                  ? {
                      title: t("error.load"),
                      onRetry: webhooks.reload,
                      retryLabel: t("error.retry"),
                    }
                  : undefined
              }
              empty={{ title: t("connector.webhooksEmpty") }}
            />
          )}
        </Card>
      ) : null}
    </Page>
  );
}
